import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@nx-fe/services';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class HttpTokenInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private translateSvc: TranslateService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headersConfig = {};
    if (!req.headers.has('Content-Type') && !req.headers.has('enctype')) {
      headersConfig = {
        'Content-Type': 'application/json',
      };
      const currLanguage = this.translateSvc.currentLang;
      if (currLanguage) {
        headersConfig['Accept-Language'] = currLanguage;
      }
    }

    if (req.url.indexOf('account/appSettings') === 0) {
      return next.handle(req);
    }

    const request = req.clone({ setHeaders: headersConfig, withCredentials: true });

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        return from(this.authenticationService.getLoginUrlAsync()).pipe(switchMap(loginUrl => {
          if (err.status === 401) {
            const sessionInactive = err.error && err.error.errorCode === 'E002';
            this.handleExpiredSession(loginUrl, sessionInactive);
          } else if (err.status === 400 && err.url.indexOf('/login') !== -1) {
            // We have a failed token request. This can occur in either of these two scenarios:
            // 1. We have attempted to refresh access token by using refresh token
            //    which has failed
            // 2. We failed password grant authentication (in case of multi-MC environment)

            if (err?.error?.error !== 'invalid_grant') {
              // This is case 2: throw and let log-in component handle the error
              return throwError(err);
            } else {
              // This is case 1 (invalid_grant)

              // Which means we have attempted to use
              // refresh token to get another set of identity and access tokens.
              // IdentityServer returns 400 when refresh token is expired and indcating
              // error as invalid_grant. Since refresh token is also invalid, redirect
              // to the log-in page to authenticate the user.

              const expiredSession = this.authenticationService.isExpiredSession;
              console.log('http.token on 400 -> Expired session', expiredSession, loginUrl);
              if (!expiredSession) {
                this.authenticationService.isExpiredSession = true;
                this.router.navigate(['/expiredSession'], {
                  queryParams: { redirectUrl: encodeURI(loginUrl) },
                });
              }
            }
          }
          return throwError(err);
        }));
      })
    );
  }

  handleExpiredSession(loginUrl: string, sessionInactive: boolean) {
    // back-end indicates the session is no longer authenticated, redirect to splash page only once
    // in case there are multiple calls in flight that may result in more 401 errors.
    const activeSession = this.authenticationService.isActiveSession;
    const expiredSession = this.authenticationService.isExpiredSession;

    if (activeSession && window.location.href.indexOf('signOut') == -1) {
      this.authenticationService.isActiveSession = false;

      if (!expiredSession && sessionInactive) {
        this.authenticationService.isExpiredSession = true;
        this.router.navigate(['/expiredSession'], {
          queryParams: { redirectUrl: encodeURI(loginUrl) },
        });
      } else {
        // When we are already on the log-in page and any authenticated request
        // triggers 401 (because we are not yet authenticated) then we should prevent
        // from redirecting again, because loginURL will capture current URL are redirect
        // and ultimately take us back to login page (now with lest login redirect) hence
        // we should trigger no redirects if we are already on the login page
        if (window.location.href.indexOf('/login') == -1) {
          if (!this.authenticationService.logOutInProgress) {
            window.location.href = loginUrl;
          }
        }
      }
    }
  }
}
